import { Users, Package, Layers, Stethoscope, ListChecks, DollarSign } from 'lucide-react'
import type { PackagesListResponse } from '@/lib/api-client'
import { formatCurrency } from '@/lib/utils'

interface Props {
  data: PackagesListResponse | undefined
  isLoading: boolean
}

function Card({ icon: Icon, label, value, hint, accent }: { icon: typeof Users; label: string; value: React.ReactNode; hint?: string; accent?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wide text-text-muted">{label}</span>
        <Icon className="w-4 h-4 text-text-muted" />
      </div>
      <div className={`text-2xl font-bold ${accent ?? 'text-text-dark'}`}>{value}</div>
      {hint && <div className="text-xs text-text-muted mt-1">{hint}</div>}
    </div>
  )
}

export function PackageSummaryCards({ data, isLoading }: Props) {
  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-4 h-[88px] animate-pulse" />
        ))}
      </div>
    )
  }

  const s = data.summary
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      <Card icon={Users} label="Patients" value={s.patients.toLocaleString()} hint="with outstanding" />
      <Card icon={Package} label="Packages" value={s.packages.toLocaleString()} hint="package invoices" />
      <Card icon={ListChecks} label="Open Items" value={s.outstandingItems.toLocaleString()} hint="balance > 0" />
      <Card icon={Stethoscope} label="Treatment Sessions" value={s.treatmentBalance.toLocaleString()} accent="text-info" hint="call-list priority" />
      <Card icon={Layers} label="All Sessions" value={s.totalBalance.toLocaleString()} hint="incl. consult + FOC" />
      <Card icon={DollarSign} label="Unearned Liability" value={formatCurrency(s.unearnedRevenue)} accent="text-warning" hint="value not yet redeemed" />
    </div>
  )
}
